
import { completeTime, maskTime } from '../TimeInput/time-mask';

export const WEEKDAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'] as const;

export const WEEKS_IN_GRID = 6;

export interface Ymd {
  year: number;
  month: number;
  day: number;
}

const pad = (value: number, size = 2) => String(value).padStart(size, '0');

export function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

export function toIso({ year, month, day }: Ymd): string {
  return `${pad(year, 4)}-${pad(month)}-${pad(day)}`;
}

export function parseIso(value: string): Ymd | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match == null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
}

export function isIsoDate(value: string): boolean {
  return parseIso(value) != null;
}

export function toLocalDate(value: string): Date | null {
  const parts = parseIso(value);
  if (parts == null) return null;
  return new Date(parts.year, parts.month - 1, parts.day);
}

export function fromLocalDate(date: Date): string {
  return toIso({ year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() });
}

export function isoToday(): string {
  return fromLocalDate(new Date());
}

export function addDays(value: string, delta: number): string {
  const parts = parseIso(value);
  if (parts == null) return value;
  return fromLocalDate(new Date(parts.year, parts.month - 1, parts.day + delta));
}

export function addMonths(value: string, delta: number): string {
  const parts = parseIso(value);
  if (parts == null) return value;
  const shifted = new Date(parts.year, parts.month - 1 + delta, 1);
  const year = shifted.getFullYear();
  const month = shifted.getMonth() + 1;
  return toIso({ year, month, day: Math.min(parts.day, daysInMonth(year, month)) });
}

export function startOfMonth(value: string): string {
  const parts = parseIso(value);
  if (parts == null) return value;
  return toIso({ ...parts, day: 1 });
}

export function isSameMonth(a: string, b: string): boolean {
  return a.slice(0, 7) === b.slice(0, 7);
}

export function weekdayIndex(value: string): number {
  const date = toLocalDate(value);
  if (date == null) return 0;
  return (date.getDay() + 6) % 7;
}

export function isWeekend(value: string): boolean {
  return weekdayIndex(value) >= 5;
}

export function monthGrid(value: string): string[][] {
  const first = startOfMonth(value);
  const start = addDays(first, -weekdayIndex(first));
  const weeks: string[][] = [];
  for (let week = 0; week < WEEKS_IN_GRID; week += 1) {
    const days: string[] = [];
    for (let day = 0; day < 7; day += 1) days.push(addDays(start, week * 7 + day));
    weeks.push(days);
  }
  return weeks;
}

export function compareIso(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function clampIso(value: string, min?: string, max?: string): string {
  if (min != null && min !== '' && compareIso(value, min) < 0) return min;
  if (max != null && max !== '' && compareIso(value, max) > 0) return max;
  return value;
}

export function isBetween(value: string, from: string, to: string): boolean {
  if (from === '' || to === '') return false;
  const [start, end] = compareIso(from, to) <= 0 ? [from, to] : [to, from];
  return compareIso(value, start) >= 0 && compareIso(value, end) <= 0;
}

export function monthLabel(value: string): string {
  const date = toLocalDate(value) ?? new Date();
  const month = date.toLocaleDateString('ru', { month: 'long' });
  return `${month} ${date.getFullYear()}`;
}

export function monthNames(): string[] {
  return Array.from({ length: 12 }, (_, index) =>
    new Date(2000, index, 1).toLocaleDateString('ru', { month: 'long' }),
  );
}

export function formatRuLong(value: string): string {
  const date = toLocalDate(value);
  if (date == null) return value;
  return date.toLocaleDateString('ru', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function formatRu(value: string): string {
  const parts = parseIso(value);
  if (parts == null) return '';
  return `${pad(parts.day)}.${pad(parts.month)}.${pad(parts.year, 4)}`;
}

export function parseRu(text: string): string {
  const digits = text.replace(/\D/g, '');
  if (digits.length !== 8) return '';
  const iso = `${digits.slice(4, 8)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}`;
  return isIsoDate(iso) ? iso : '';
}

export function maskDate(input: string): string {
  const digits = input.replace(/\D/g, '').slice(0, 8);
  if (digits.length === 0) return '';

  if (digits.length === 1) return Number(digits) > 3 ? `0${digits}.` : digits;

  let dd = digits.slice(0, 2);
  if (Number(dd) > 31) dd = '31';
  if (Number(dd) === 0) dd = '01';

  const afterDay = digits.slice(2);
  if (afterDay.length === 0) return `${dd}.`;
  if (afterDay.length === 1) {
    return Number(afterDay) > 1 ? `${dd}.0${afterDay}.` : `${dd}.${afterDay}`;
  }

  let mm = afterDay.slice(0, 2);
  if (Number(mm) > 12) mm = '12';
  if (Number(mm) === 0) mm = '01';

  const yyyy = afterDay.slice(2);
  return yyyy.length === 0 ? `${dd}.${mm}.` : `${dd}.${mm}.${yyyy}`;
}

export function maskDateEdit(next: string, previous: string): string {
  const deleting = next.length < previous.length;
  const digits = next.replace(/\D/g, '');
  const masked = maskDate(digits);
  if (deleting && masked === previous) return maskDate(digits.slice(0, -1));
  return masked;
}

export function completeDate(text: string, today = isoToday()): string {
  const digits = text.replace(/\D/g, '');
  if (digits.length === 0) return '';

  const now = parseIso(today) ?? { year: new Date().getFullYear(), month: 1, day: 1 };
  const dayDigits = digits.slice(0, 2);
  const monthDigits = digits.slice(2, 4);
  const yearDigits = digits.slice(4, 8);

  const month = Math.min(Math.max(Number(monthDigits || now.month), 1), 12);
  const yearPrefix = pad(now.year, 4).slice(0, 4 - yearDigits.length);
  const year = yearDigits.length === 0 ? now.year : Number(yearPrefix + yearDigits);
  const day = Math.min(Math.max(Number(dayDigits), 1), daysInMonth(year, month));

  return `${pad(day)}.${pad(month)}.${pad(year, 4)}`;
}

export function stepDate(value: string, deltaDays: number, base = isoToday()): string {
  const from = parseRu(completeDate(value)) || base;
  return formatRu(addDays(from, deltaDays));
}


export interface DateTimeParts {
  date: string;
  time: string;
}

export function isLocalTime(value: string): boolean {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  return match != null && Number(match[1]) < 24 && Number(match[2]) < 60;
}

export function splitDateTime(value: string): DateTimeParts {
  const [date = '', time = ''] = value.split('T');
  return { date: isIsoDate(date) ? date : '', time: isLocalTime(time) ? time : '' };
}

export function joinDateTime({ date, time }: DateTimeParts): string {
  if (date === '') return '';
  return `${date}T${time === '' ? '00:00' : time}`;
}


export const RANGE_SEPARATOR = ' — ';

export interface DateRange {
  from: string;
  to: string;
}

export function maskRange(input: string): string {
  const digits = input.replace(/\D/g, '').slice(0, 16);
  const head = maskDate(digits.slice(0, 8));
  if (digits.length <= 8) return head;
  return `${head}${RANGE_SEPARATOR}${maskDate(digits.slice(8))}`;
}

export function maskRangeEdit(next: string, previous: string): string {
  const deleting = next.length < previous.length;
  const digits = next.replace(/\D/g, '');
  const masked = maskRange(digits);
  if (deleting && masked === previous) return maskRange(digits.slice(0, -1));
  return masked;
}

export function completeRange(text: string, today = isoToday()): string {
  const digits = text.replace(/\D/g, '');
  if (digits.length === 0) return '';
  const head = completeDate(digits.slice(0, 8), today);
  if (digits.length <= 8) return head;
  const tail = completeDate(digits.slice(8), parseRu(head) || today);
  return `${head}${RANGE_SEPARATOR}${tail}`;
}

export function parseRuRange(text: string): DateRange {
  const digits = text.replace(/\D/g, '');
  return {
    from: parseRu(digits.slice(0, 8)),
    to: digits.length > 8 ? parseRu(digits.slice(8, 16)) : '',
  };
}

export function formatRuRange({ from, to }: DateRange): string {
  if (from === '' && to === '') return '';
  if (to === '') return formatRu(from);
  if (from === '') return formatRu(to);
  return `${formatRu(from)}${RANGE_SEPARATOR}${formatRu(to)}`;
}

export function orderRange({ from, to }: DateRange): DateRange {
  if (from === '' || to === '' || compareIso(from, to) <= 0) return { from, to };
  return { from: to, to: from };
}

export function maskDateTime(input: string): string {
  const digits = input.replace(/\D/g, '').slice(0, 12);
  const head = maskDate(digits.slice(0, 8));
  if (digits.length <= 8) return head;
  return `${head} ${maskTime(digits.slice(8))}`;
}

export function maskDateTimeEdit(next: string, previous: string): string {
  const deleting = next.length < previous.length;
  const digits = next.replace(/\D/g, '');
  const masked = maskDateTime(digits);
  if (deleting && masked === previous) return maskDateTime(digits.slice(0, -1));
  return masked;
}

export function completeDateTime(text: string, today = isoToday()): string {
  const digits = text.replace(/\D/g, '');
  if (digits.length === 0) return '';
  const head = completeDate(digits.slice(0, 8), today);
  return `${head} ${completeTime(digits.slice(8)) || '00:00'}`;
}

export function parseRuDateTime(text: string): string {
  const digits = text.replace(/\D/g, '');
  const date = parseRu(digits.slice(0, 8));
  if (date === '') return '';
  const time = completeTime(digits.slice(8, 12)) || '00:00';
  return joinDateTime({ date, time });
}

export function formatRuDateTime(value: string): string {
  const { date, time } = splitDateTime(value);
  if (date === '') return '';
  return `${formatRu(date)} ${time === '' ? '00:00' : time}`;
}
